<template>
  <div class="mall-my-home">
    <header class="mall-my-home__head">
      <h1>마이페이지</h1>
      <p>주문내역 · 문의 · 구매후기 · 배송지를 관리합니다.</p>
    </header>

    <section v-if="todoItems.length" class="mall-my-home__todo" aria-label="처리할 일">
      <div class="mall-my-home__todo-head">
        <h2>처리할 일</h2>
        <span>{{ todoItems.length }}건</span>
      </div>
      <ul>
        <li v-for="item in todoItems" :key="item.to + item.label">
          <router-link :to="item.to">
            <AppIcon :icon="item.icon" :size="18" />
            <div>
              <strong>{{ item.label }}</strong>
              <em>{{ item.desc }}</em>
            </div>
            <b>{{ item.count }}</b>
            <AppIcon icon="mdi-chevron-right" :size="18" />
          </router-link>
        </li>
      </ul>
    </section>

    <section class="mall-my-home__summary" aria-label="쇼핑 요약">
      <router-link
        v-for="card in summaryCards"
        :key="card.to"
        :to="card.to"
        class="mall-my-home__card"
      >
        <span>{{ card.label }}</span>
        <strong>
          {{ loading ? '…' : card.value }}
          <em v-if="card.unit">{{ card.unit }}</em>
        </strong>
      </router-link>
    </section>

    <section v-if="buyAgain.length" class="mall-my-home__rail" aria-label="이전구매상품">
      <div class="mall-my-home__rail-head">
        <h2>이전구매상품</h2>
        <router-link :to="MALL_ORDERS_PATH">주문내역</router-link>
      </div>
      <div class="mall-my-home__rail-track">
        <ProductCard v-for="product in buyAgain" :key="product.id" :product="product" />
      </div>
    </section>

    <section v-if="recentProducts.length" class="mall-my-home__rail" aria-label="오늘 본 상품">
      <div class="mall-my-home__rail-head">
        <h2>오늘 본 상품</h2>
      </div>
      <div class="mall-my-home__rail-track">
        <ProductCard v-for="product in recentProducts" :key="product.id" :product="product" />
      </div>
    </section>

    <section class="mall-my-home__menus" aria-label="바로가기">
      <article v-for="group in MALL_MY_NAV" :key="group.title" class="mall-my-home__menu">
        <h2>{{ group.title }}</h2>
        <ul>
          <li v-for="item in group.items" :key="item.to">
            <router-link :to="item.to">
              {{ item.label }}
              <AppIcon icon="mdi-chevron-right" :size="18" />
            </router-link>
          </li>
        </ul>
      </article>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import { ensureLiveProducts } from '@/api/mall'
import { fetchBuyAgainProducts, fetchMyOrders } from '@/api/mallOrder'
import { toMallProduct } from '@/api/mallProduct'
import { fetchMyConsultPosts } from '@/api/consultBoard'
import { fetchMyQnas, fetchMyReviews } from '@/api/mallReview'
import { useRecentlyViewed } from '@/composables/useRecentlyViewed'
import { useWishlist } from '@/composables/useWishlist'
import { MALL_ORDERS_PATH, MALL_WISHLIST_PATH } from '@/constants/mall'
import {
  MALL_MY_CLAIMS_PATH,
  MALL_MY_CONSULTS_PATH,
  MALL_MY_NAV,
  MALL_MY_REVIEWS_PATH,
} from '@/constants/mallMy'
import { getProductById, type MallProduct } from '@/constants/mallProducts'

const { wishCount } = useWishlist()
const { recentIds } = useRecentlyViewed()

const loading = ref(true)
const orderCount = ref(0)
const claimCount = ref(0)
const reviewCount = ref(0)
const inquiryCount = ref(0)
const pendingPayCount = ref(0)
const cardPendingCount = ref(0)
const payConfirmCount = ref(0)
const confirmCount = ref(0)
const reviewableCount = ref(0)
const unansweredQna = ref(0)
const buyAgain = ref<MallProduct[]>([])

const recentProducts = computed(() =>
  recentIds.value
    .map((id) => getProductById(id))
    .filter((item): item is MallProduct => Boolean(item))
    .slice(0, 6),
)

const summaryCards = computed(() => [
  { label: '주문내역', value: orderCount.value, unit: '건', to: MALL_ORDERS_PATH },
  { label: '취소/반품', value: claimCount.value, unit: '건', to: MALL_MY_CLAIMS_PATH },
  { label: '구매후기', value: reviewCount.value, unit: '개', to: MALL_MY_REVIEWS_PATH },
  { label: '문의', value: inquiryCount.value, unit: '건', to: MALL_MY_CONSULTS_PATH },
  { label: '찜하기', value: wishCount.value, unit: '개', to: MALL_WISHLIST_PATH },
])

const todoItems = computed(() => {
  const items: Array<{
    label: string
    desc: string
    count: number
    to: string
    icon: string
  }> = []
  if (cardPendingCount.value) {
    items.push({
      label: '결제 대기 주문',
      desc: '카드·간편결제를 마쳐 주세요.',
      count: cardPendingCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-credit-card-outline',
    })
  }
  if (pendingPayCount.value) {
    items.push({
      label: '입금 대기 주문',
      desc: '계좌 이체 후 「입금했어요」를 눌러 주세요.',
      count: pendingPayCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-cash-clock',
    })
  }
  if (payConfirmCount.value) {
    items.push({
      label: '입금 확인 중',
      desc: '입금 알림을 보냈습니다. 운영팀 확인을 기다립니다.',
      count: payConfirmCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-bank-check',
    })
  }
  if (confirmCount.value) {
    items.push({
      label: '구매확정 대기',
      desc: '배송완료 상품을 확인하세요. 7일이 지나면 자동 구매확정됩니다.',
      count: confirmCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-package-check',
    })
  }
  if (reviewableCount.value) {
    items.push({
      label: '작성 가능한 구매후기',
      desc: '구매확정 상품에 구매후기를 남겨 주세요.',
      count: reviewableCount.value,
      to: MALL_MY_REVIEWS_PATH,
      icon: 'mdi-message-draw',
    })
  }
  if (unansweredQna.value) {
    items.push({
      label: '답변 대기 문의',
      desc: '답변을 기다리는 문의가 있습니다.',
      count: unansweredQna.value,
      to: MALL_MY_CONSULTS_PATH,
      icon: 'mdi-forum-outline',
    })
  }
  return items
})

onMounted(async () => {
  loading.value = true
  try {
    await ensureLiveProducts()
    const [orders, reviews, qnas, consults, again] = await Promise.all([
      fetchMyOrders().catch(() => []),
      fetchMyReviews().catch(() => []),
      fetchMyQnas().catch(() => []),
      fetchMyConsultPosts({ page: 1, limit: 100, channel: 'MALL' }).catch(() => null),
      fetchBuyAgainProducts(6).catch(() => []),
    ])
    orderCount.value = orders.length
    claimCount.value = orders.filter((order) =>
      (order.itemStatuses || []).some((status) =>
        ['CANCEL_REQUEST', 'RETURN_REQUEST', 'CANCELED'].includes(status),
      ),
    ).length
    reviewCount.value = reviews.length
    inquiryCount.value = qnas.length + (consults?.pagination.total || 0)
    unansweredQna.value =
      qnas.filter((item) => !item.answer).length +
      (consults?.items.filter((item) => item.status === 'WAIT').length || 0)
    cardPendingCount.value = orders.filter(
      (order) => order.status === 'PENDING' && order.payMethod === 'TOSS',
    ).length
    pendingPayCount.value = orders.filter(
      (order) => order.status === 'PENDING' && order.payMethod !== 'TOSS' && !order.payNotifiedAt,
    ).length
    payConfirmCount.value = orders.filter(
      (order) => order.status === 'PENDING' && Boolean(order.payNotifiedAt),
    ).length

    const reviewed = new Set(reviews.map((item) => item.productCode))
    let confirm = 0
    let reviewable = 0
    orders.forEach((order) => {
      const statuses = order.itemStatuses || []
      const codes = order.itemProductCodes || []
      statuses.forEach((status, index) => {
        if (order.status === 'PAID' && status === 'DELIVERED') confirm += 1
        const productCode = codes[index]
        if (status === 'DONE' && productCode && !reviewed.has(productCode)) reviewable += 1
      })
    })
    confirmCount.value = confirm
    reviewableCount.value = reviewable
    buyAgain.value = again.map(toMallProduct)
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" src="./MallMyHomePage.scss"></style>
